import { AssistantRuntimeProvider, ChatModelAdapter, useAuiState } from "@assistant-ui/react";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { ThreadHistoryProvider, ThreadNotFoundError } from "./threadHistoryProvider";
import { useLocalRuntime } from "@assistant-ui/react";
import { RemoteThreadListAdapter } from "@assistant-ui/react";
import { createAssistantStream } from "assistant-stream";
import { useRemoteThreadListRuntime } from "@assistant-ui/react";
import { createPraetorAttachmentAdapter } from "@/lib/attachments/praetorAttachmentAdapter";
import {
  readLaunchCaseIdFromSession,
  wasLaunchedWithoutCase,
  RouteContextValues,
  useRouteContext,
} from "../contexts/routeContext";
import { ThreadContextValues, useThreadContext } from "../contexts/threadContext";
import { apiAuthFetch } from "@/lib/authFetch";
import { appendLocParam } from "@/lib/i18n";
import { ClientLogger } from "@/lib/clientLogger";
import { DEFAULT_CHAT_TITLE } from "@/lib/constants";
import { appEvents } from "@/lib/events";
import { setAuiRemoteThreadId } from "@/lib/auiThreadIdBridge";
import { usePendingNewThreadCaseIdStore } from "@/components/assistant-ui/pendingNewThreadStores";
import { setLastViewedBranch } from "@/lib/storage";
import { useAnonymizationStore } from "@/components/custom/stores/anonymizationStore";
import { StreamVisibilityManager } from "@/lib/streamVisibilityManager";
import { toast } from "sonner";

type ThreadActions = {
  pin: (remoteId: string) => Promise<void>;
  unpin: (remoteId: string) => Promise<void>;
  rename: (remoteId: string, title: string) => Promise<void>;
  archive: (remoteId: string) => Promise<void>;
  delete: (remoteId: string) => Promise<void>;
};

const ThreadActionsContext = createContext<ThreadActions | null>(null);

export function useThreadActions(): ThreadActions {
  const ctx = useContext(ThreadActionsContext);
  if (!ctx) throw new Error("useThreadActions must be used inside CustomThreadListProvider");
  return ctx;
}

export default function CustomThreadListProvider({
  children,
  modelAdapter,
}: Readonly<{
  children: ReactNode;
  modelAdapter: ChatModelAdapter;
}>) {
  const threadContext = useThreadContext();
  const routeContext = useRouteContext();

  // Refs so the adapter methods always read current context at call-time
  // without the adapter itself needing to be recreated on every context change.
  const routeContextRef = useRef(routeContext);
  useEffect(() => {
    routeContextRef.current = routeContext;
  }, [routeContext]);

  const threadContextRef = useRef(threadContext);
  useEffect(() => {
    threadContextRef.current = threadContext;
  }, [threadContext]);

  // Stable adapter — created once. All volatile state is accessed via refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const threadListAdapter: RemoteThreadListAdapter = useMemo(() => createThreadListAdapter(threadContextRef, routeContextRef, ThreadHistoryProvider), []);

  // Stable attachment adapter — captures routeContextRef.current at call-time so
  // it always reads the live thread id without re-creating the adapter on every
  // route change (which would force useLocalRuntime to rebuild its scope).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const attachmentAdapter = useMemo(() => createPraetorAttachmentAdapter(
    () => routeContextRef.current.threadId ?? null,
  ), []);

  const runtimeHook = useCallback(
    () => useLocalRuntime(modelAdapter, { adapters: { attachments: attachmentAdapter } }),
    [modelAdapter, attachmentAdapter],
  );

  const runtime = useRemoteThreadListRuntime({
    runtimeHook,
    adapter: threadListAdapter,
    // Drive thread selection from URL — the library switches automatically
    // when this value changes, eliminating the need for a Route→AUI sync
    // effect in RouteWatcher.
    threadId: routeContext.threadId,
  });

  // Force the library to re-invoke adapter.list() so the thread list reflects
  // server-side ordering changes (e.g. pin/unpin).  No public reload() API
  // exists as of @assistant-ui/react 0.12.25 — this reaches into the private
  // core.  Guard defensively so a future upgrade that removes these internals
  // degrades to a no-op (stale order until next natural refresh) instead of
  // crashing.
  // TODO: replace with public API when available (https://github.com/Yonom/assistant-ui/issues/XXX)
  const reloadThreadList = useCallback(() => {
    try {
      const core = (runtime.threads as Record<string, any>)._core;
      if (!core || typeof core.__internal_load !== "function") return;
      core._loadThreadsPromise = undefined;
      core.__internal_load();
    } catch (e) {
      ClientLogger.debug("Thread list reload hack failed", {
        error: e, component: "CustomThreadListProvider",
      });
    }
  }, [runtime]);

  const threadActions = useMemo<ThreadActions>(() => ({
    pin: async (remoteId: string) => {
      const res = await apiAuthFetch(`api/threads/${remoteId}/pin`, { method: "POST" });
      if (!res.ok) {
        ClientLogger.error("Pin thread failed", {
          action: `POST /threads/${remoteId}/pin`,
          component: "ThreadActions",
          metadata: { status: res.status },
        });
        return;
      }
      threadContextRef.current.onThreadStateChanged({
        threadItems: threadContextRef.current.threadItems.map(item =>
          item.remoteId === remoteId ? { ...item, isPinned: true } : item
        ),
      });
      reloadThreadList();
    },
    unpin: async (remoteId: string) => {
      const res = await apiAuthFetch(`api/threads/${remoteId}/unpin`, { method: "POST" });
      if (!res.ok) {
        ClientLogger.error("Unpin thread failed", {
          action: `POST /threads/${remoteId}/unpin`,
          component: "ThreadActions",
          metadata: { status: res.status },
        });
        return;
      }
      threadContextRef.current.onThreadStateChanged({
        threadItems: threadContextRef.current.threadItems.map(item =>
          item.remoteId === remoteId ? { ...item, isPinned: false } : item
        ),
      });
      reloadThreadList();
    },
    rename: async (remoteId: string, title: string) => {
      const res = await apiAuthFetch(`api/threads/${remoteId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      if (!res.ok) {
        ClientLogger.error("Rename thread failed", {
          action: `PATCH /threads/${remoteId}`,
          component: "ThreadActions",
          metadata: { status: res.status },
        });
        toast.error(
          res.status === 409
            ? "Konverzace byla právě aktualizována. Zkuste to znovu."
            : "Přejmenování konverzace se nezdařilo."
        );
        return;
      }
      const now = new Date().toISOString();
      threadContextRef.current.onThreadStateChanged({
        threadItems: threadContextRef.current.threadItems.map(item =>
          item.remoteId === remoteId ? { ...item, title, updatedAt: now } : item
        ),
      });
      reloadThreadList();
    },
    archive: async (remoteId: string) => {
      const res = await apiAuthFetch(`api/threads/${remoteId}/archive`, { method: "POST" });
      if (!res.ok) {
        ClientLogger.error("Archive thread failed", {
          action: `POST /threads/${remoteId}/archive`,
          component: "ThreadActions",
          metadata: { status: res.status },
        });
        return;
      }
      const now = new Date().toISOString();
      threadContextRef.current.onThreadStateChanged({
        threadItems: threadContextRef.current.threadItems.map(item =>
          item.remoteId === remoteId ? { ...item, archivedAt: now } : item
        ),
      });
      reloadThreadList();
    },
    delete: async (remoteId: string) => {
      const res = await apiAuthFetch(`api/threads/${remoteId}`, { method: "DELETE" });
      if (!res.ok) {
        ClientLogger.error("Delete thread failed", {
          action: `DELETE /threads/${remoteId}`,
          component: "ThreadActions",
          metadata: { status: res.status },
        });
        return;
      }
      setLastViewedBranch(remoteId, null);
      threadContextRef.current.onThreadStateChanged({
        threadItems: threadContextRef.current.threadItems.filter(
          item => item.remoteId !== remoteId
        ),
      });
      reloadThreadList();
    },
  }), [reloadThreadList]);

  // Re-fetch the thread list when the active thread's UpdatedAt has shifted on
  // the server: on send-ack (backend has committed the user-message INSERT +
  // thread bump in one transaction) and again on turn-completed (catches any
  // generated-document bumps that happened during the turn).
  useEffect(() => {
    const offSent = appEvents.on("user-message-sent", () => reloadThreadList());
    const offDone = appEvents.on("turn-completed", () => reloadThreadList());
    return () => {
      offSent();
      offDone();
    };
  }, [reloadThreadList]);

  return (
    <ThreadActionsContext.Provider value={threadActions}>
      <AssistantRuntimeProvider runtime={runtime}>
        <ThreadListDedupWatcher reloadThreadList={reloadThreadList} />
        <AuiThreadIdBridge />
        {children}
      </AssistantRuntimeProvider>
    </ThreadActionsContext.Provider>
  );
}

// Mirrors AUI's threadListItem.remoteId into a module-scoped getter so
// non-React code (chat model adapter, in particular waitForInitializedThreadId)
// can read the freshest value the moment AUI publishes it. The chat adapter
// previously polled routeContext.threadId, which only updates after AUI's
// state change has propagated through useAuiState → RouteWatcher → React
// re-render — a one-cycle delay we don't need to pay on every send.
function AuiThreadIdBridge() {
  const remoteId = useAuiState(state => state.threadListItem.remoteId);
  useEffect(() => {
    setAuiRemoteThreadId(remoteId ?? undefined);
    return () => {
      // Defensive: if this bridge unmounts (e.g. provider re-mount on hot
      // reload) clear the stale module value so the next adapter wait
      // doesn't read a value from a torn-down runtime tree.
      setAuiRemoteThreadId(undefined);
    };
  }, [remoteId]);
  return null;
}

// Workaround for assistant-ui 0.12.25 race: __internal_load() fires both
// adapter.list() and switchToThread(threadId) concurrently. switchToThread's
// fetch() path appends remoteId to threadIds without checking for existence,
// so if list() already placed it there we get a duplicate ThreadListItem.
// Detect duplicates reactively via the public store and trigger a clean
// list()-only reload (safe because _initialThreadLoaded is already true).
// Rendered inside AssistantRuntimeProvider so useAuiState has access to the store.
// TODO: remove when assistant-ui ships a dedup fix in switchToThread.
function ThreadListDedupWatcher({ reloadThreadList }: { reloadThreadList: () => void }) {
  const threadIds = useAuiState(state => state.threads.threadIds);
  const dedupReloadCount = useRef(0);
  useEffect(() => {
    if (new Set(threadIds).size >= threadIds.length) return;
    if (dedupReloadCount.current >= 3) return;
    dedupReloadCount.current++;
    reloadThreadList();
  }, [threadIds, reloadThreadList]);
  return null;
}

function createThreadListAdapter(
  threadContextRef: { current: ThreadContextValues },
  routeContextRef: { current: RouteContextValues },
  historyProvider: React.FC
): RemoteThreadListAdapter {
  return {
    unstable_Provider: historyProvider,
    async list() {
      // Snapshot current context at call-time, not at adapter-creation time.
      const routeState = routeContextRef.current;
      const threadState = threadContextRef.current;
      const loadedThreads: RemoteThreadDetails[] = [];
      try {
        // Settings/chats routes hydrate launchCaseId in an effect; list()
        // can run first, so read the session launch case synchronously too.
        // When the session was explicitly launched without a case ("default"),
        // never fall through to routeState.caseId — that would filter the
        // sidebar when navigating into a thread that belongs to a case.
        const filterCaseId = wasLaunchedWithoutCase()
          ? undefined
          : (routeState.launchCaseId ??
            routeState.caseId ??
            readLaunchCaseIdFromSession());
        const url = filterCaseId
          ? `api/threads?caseId=${encodeURIComponent(filterCaseId)}`
          : "api/threads";
        const response = await apiAuthFetch(url);
        const threads: RemoteThreadDetails[] = await response.json();
        loadedThreads.push(...threads);

        // Seed streaming indicators from server state (fresh app open).
        const streamingIds = threads
          .filter(t => t.is_response_completed === false)
          .map(t => t.id);
        if (streamingIds.length > 0) {
          StreamVisibilityManager.seedActiveFromServer(streamingIds);
        }

        return {
          threads: threads.map((thread) => ({
            remoteId: thread.id,
            externalId: thread.external_id ?? undefined,
            status: thread.archived_at != null ? "archived" : "regular",
            title: thread.title
          })),
        };
      }
      finally {
        threadState.onThreadStateChanged({
          threadItems: loadedThreads.map(thread => ({
            remoteId: thread.id,
            selectedDocs: thread.selectedDocs ?? [],
            isPinned: thread.is_pinned ?? false,
            createdAt: thread.created_at,
            updatedAt: thread.updated_at,
            archivedAt: thread.archived_at ?? null,
            title: thread.title,
            caseId: thread.case_id ?? null,
            mainCaseId: thread.main_case_id ?? null,
            additionalCaseIds: thread.additional_case_ids ?? null,
            additionalDocumentsByCase: thread.additional_documents_by_case ?? null,
          })),
          isListLoading: false,
        });
      }
    },

    async initialize(localId) {
        // Snapshot current route state for the values needed at initialization time.
        // Prefer launchCaseId to ensure new threads always belong to the launch case.
        const { launchCaseId, caseId, docIds } = routeContextRef.current;
        // Drain the synchronous handoff from the suggestion handler. This survives
        // the React commit + passive-effect race that a setTimeout(0) wait does not.
        // Precedence: launchCaseId still wins (session-level case context);
        // pendingCaseId beats route.caseId because it's the freshest user
        // intent and is consumed exactly once.
        const pendingCaseId = usePendingNewThreadCaseIdStore.getState().consume();
        const effectiveCaseId =
          launchCaseId ?? pendingCaseId ?? caseId ?? readLaunchCaseIdFromSession();

        // Welcome-screen anonymisation preference — always a concrete bool now (the store
        // initialises to `false`, matching the server default). The backend persists
        // exactly this value onto the new thread.
        const newThreadAnonymization =
          useAnonymizationStore.getState().newThread.enabled;

        const response = await apiAuthFetch(`api/threads`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            localId: localId,
            caseId: effectiveCaseId,
            launchCaseId: effectiveCaseId,
            docIds: docIds ?? [],
            anonymizationEnabled: newThreadAnonymization,
          }),
        });

        const result: CreateThreadResponse = await response.json();

        // Mirror the create response into the per-thread store slot so the toggle card
        // continues to reflect the user's preference seamlessly once the threadId
        // materialises, then clear the pre-creation slot for the next new thread.
        useAnonymizationStore.getState().set(result.id, {
          enabled: result.anonymizationEnabled === true,
          everAnonymized: result.everAnonymized === true,
        });
        useAnonymizationStore.getState().resetNewThread();

        // Merge server's selectedDocs with the current optimistic view keyed by docId.
        // Picker-added picks before first send live in selectedDocs + a pending buffer
        // (thread.tsx); the buffer flushes once threadListItem.remoteId turns real. If
        // we blindly replaced selectedDocs with result.selectedDocs, those optimistic
        // chips would disappear until the flush POST round-trips. Server wins on
        // duplicates so real server-side metadata overrides optimistic entries.
        const optimistic = threadContextRef.current.selectedDocs ?? [];
        const serverIds = new Set(result.selectedDocs.map(d => d.docId));
        const mergedSelectedDocs: RemoteThreadDocument[] = [
          ...result.selectedDocs,
          ...optimistic.filter(
            (d): d is RemoteThreadDocument => !serverIds.has(d.docId) && d.name !== undefined,
          ),
        ];

        threadContextRef.current.onThreadStateChanged({
            threadItems: [...threadContextRef.current.threadItems.filter(item => item.remoteId !== result.id), { remoteId: result.id, selectedDocs: mergedSelectedDocs }],
            selectedDocs: mergedSelectedDocs,
          });

        // Route update is handled by RouteWatcher's AUI→route effect once AUI sets
        // threadListItem.remoteId after this function returns. No explicit call needed.
        return { remoteId: result.id, externalId: result.externalId };
      },

    async rename(remoteId, title) {
      await apiAuthFetch(`api/threads/${remoteId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
    },

    async archive(remoteId) {
      await apiAuthFetch(`api/threads/${remoteId}/archive`, { method: "POST" });
    },

    async unarchive(remoteId) {
      await apiAuthFetch(`api/threads/${remoteId}/unarchive`, { method: "POST" });
    },

    async delete(remoteId) {
      await apiAuthFetch(`api/threads/${remoteId}`, { method: "DELETE" });
      // Clear the stale leaf cache so the next page load doesn't fire a PATCH
      // against a deleted thread and generate a 404 storm.
      setLastViewedBranch(remoteId, null);
    },

    async fetch(remoteId) {
      const response = await apiAuthFetch(appendLocParam(`api/threads/${remoteId}`));
      if (response.status === 404) throw new ThreadNotFoundError(remoteId);
      if (!response.ok) throw new Error(`Failed to fetch thread: ${response.status}`);
      const thread: RemoteThreadDetails = await response.json();

      // Populate the anonymization store so the composer toggle reflects the persisted
      // per-thread state immediately on cold load. The backend column is non-nullable
      // (project default `false`), so a missing field on the wire collapses to `false`
      // — the safe-degraded default that matches the server policy.
      useAnonymizationStore.getState().set(thread.id, {
        enabled: thread.anonymization_enabled === true,
        everAnonymized: thread.ever_anonymized === true,
      });

      return {
        status: thread.archived_at != null ? "archived" : "regular",
        externalId: thread.id,
        remoteId: thread.id,
        title: thread.title,
      };
    },

    async generateTitle(remoteId, messages) {
      return createAssistantStream(async (controller) => {
        let lang = "cs";
        try { lang = sessionStorage.getItem("praetor:lang") ?? "cs"; } catch { /* sessionStorage unavailable */ }

        const response = await apiAuthFetch(`api/threads/${remoteId}/title`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ messages, lang }),
        });

        if (!response.ok) {
          ClientLogger.warn("Title generation failed", {
            action: `POST /threads/${remoteId}/title`,
            component: "generateTitle",
            metadata: { status: response.status },
          });
          controller.appendText(DEFAULT_CHAT_TITLE);
          controller.close();
          return;
        }

        const { title } = await response.json();
        controller.appendText(title ?? DEFAULT_CHAT_TITLE);
        controller.close();
      });
    }
  }
};



type CreateThreadResponse = {
  id: string;
  externalId?: string;
  caseId?: string;
  selectedDocs: RemoteThreadDocument[];
  /** Persisted per-thread anonymisation state (mirrors backend CreateThreadResponse — always a concrete bool). */
  anonymizationEnabled?: boolean;
  /** Always false on create; included for shape symmetry with PATCH + list responses. */
  everAnonymized?: boolean;
};

export type RemoteThreadDetails = {
  id: string;
  external_id?: string | null;
  case_id?: string | null;
  title: string;
  archived_at: string | null;
  /** ISO-8601 (UTC) creation timestamp. Set by the backend on every list/detail response. */
  created_at?: string;
  /** ISO-8601 (UTC) last-update timestamp. Powers chats-page time-bucket grouping. */
  updated_at?: string;
  is_pinned?: boolean;
  /** Whether the thread's response is fully completed (false = still streaming). */
  is_response_completed?: boolean;
  selectedDocs?: RemoteThreadDocument[];
  /** Per-thread anonymization state — always a concrete bool when present. */
  anonymization_enabled?: boolean;
  /** True once at least one prior turn was successfully anonymised. Drives the flip-off warning. */
  ever_anonymized?: boolean;
  /** Primary case anchor (GUID). Null = workspace-level thread. */
  main_case_id?: string | null;
  /** Additional case IDs approved via elicitation or scope endpoint. */
  additional_case_ids?: string[] | null;
  /** Per-case document restrictions. Key = caseId, value = approved docId list. */
  additional_documents_by_case?: Record<string, string[]> | null;
};

export type RemoteThreadDocument = {
  docId: string;
  name: string;
  caseId?: string | null;
  /** Lower-case file extension token from backend (Praetor MCP "type" or filename
   *  extension fallback). Null/undefined when neither source resolved. */
  type?: string | null;
};
